/**
 * Prefstest Notice Bar
 * ─────────────────
 * Manages dismiss behavior (with sessionStorage persistence) and entrance
 * animations for the notice bar. Uses event delegation for efficiency.
 * No dependencies.
 */
(function () {
  var STORAGE_PREFIX = 'prefstest-notice-bar-dismissed-';

  /**
   * Check if a notice bar was previously dismissed this session.
   */
  function isDismissed(id) {
    try {
      return sessionStorage.getItem(STORAGE_PREFIX + id) === '1';
    } catch (e) {
      return false;
    }
  }

  /**
   * Mark a notice bar as dismissed for this session.
   */
  function setDismissed(id) {
    try {
      sessionStorage.setItem(STORAGE_PREFIX + id, '1');
    } catch (e) {
      // sessionStorage unavailable — dismiss still works visually
    }
  }

  /**
   * Initialize a notice bar element: check dismissed state, run entrance animation.
   */
  function initNoticeBar(bar) {
    var id = bar.getAttribute('data-prefstest-notice-bar-id');
    var isDismissibleBar = bar.hasAttribute('data-prefstest-notice-bar-dismissible');
    var animation = bar.getAttribute('data-prefstest-notice-bar-animation');

    // In design mode: force visible, skip dismiss persistence and animations
    if (window.Shopify && window.Shopify.designMode) {
      bar.hidden = false;
      bar.classList.add('prefstest-notice-bar--visible');
      return;
    }

    // If previously dismissed, hide immediately
    if (isDismissibleBar && id && isDismissed(id)) {
      bar.hidden = true;
      return;
    }

    // Trigger entrance animation
    if (animation) {
      // Use rAF to ensure the initial hidden state is painted before animating in
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          bar.classList.add('prefstest-notice-bar--visible');
        });
      });
    }
  }

  /**
   * Dismiss a notice bar with exit animation.
   */
  function dismissBar(bar) {
    // In design mode: prevent dismissal so merchants can always see the bar
    if (window.Shopify && window.Shopify.designMode) return;

    var id = bar.getAttribute('data-prefstest-notice-bar-id');
    var animation = bar.getAttribute('data-prefstest-notice-bar-animation');

    if (id) {
      setDismissed(id);
    }

    if (animation) {
      bar.classList.remove('prefstest-notice-bar--visible');
      bar.classList.add('prefstest-notice-bar--dismissing');
      bar.addEventListener('transitionend', function handler() {
        bar.removeEventListener('transitionend', handler);
        bar.hidden = true;
      });
      // Fallback: hide after 500ms if transitionend doesn't fire
      setTimeout(function () {
        bar.hidden = true;
      }, 500);
    } else {
      bar.hidden = true;
    }
  }

  /**
   * Initialize ticker marquee: calculate animation duration from track width and speed.
   */
  function initTicker(ticker) {
    var track = ticker.querySelector('[data-ticker-track]');
    if (!track) return;

    var speed = parseFloat(ticker.getAttribute('data-ticker-speed')) || 40;
    var gap = parseFloat(ticker.getAttribute('data-ticker-gap')) || 60;

    // Set gap as CSS variable
    ticker.style.setProperty('--ticker-gap', gap + 'px');

    function updateDuration() {
      // Total track width / 2 gives one full cycle (since animation moves -50%)
      var trackWidth = track.scrollWidth / 2;
      if (trackWidth <= 0) return;
      var duration = trackWidth / speed;
      ticker.style.setProperty('--ticker-duration', duration + 's');
    }

    updateDuration();

    // Recalculate on resize (font size changes, etc.)
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(updateDuration, 200);
    });
  }

  // Initialize all notice bars on DOM ready
  function initAll() {
    var bars = document.querySelectorAll('[data-prefstest-notice-bar-id]');
    for (var i = 0; i < bars.length; i++) {
      initNoticeBar(bars[i]);
    }

    // Initialize tickers
    var tickers = document.querySelectorAll('[data-prefstest-ticker]');
    for (var j = 0; j < tickers.length; j++) {
      initTicker(tickers[j]);
    }
  }

  // Event delegation for dismiss buttons
  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-prefstest-notice-bar-dismiss]');
    if (!button) return;

    var bar = button.closest('[data-prefstest-notice-bar-id]');
    if (!bar) return;

    dismissBar(bar);
  });

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
